const section1 = 'sections/perf-section1.png';
const section2 = 'sections/perf-section2.png';
const section3 = 'sections/perf-section3.png';
const section4 = 'sections/perf-section4.png';
const section5 = 'sections/perf-section5.png';

const performanceEnggContent = {
  bannerText: {
    heading: 'GAINSS:Performance Engineering for the Cloud-Native Applications',
    items: [
      {
        title: 'Beyond Load Testing – Measure Real User Experience',
        info: 'Traditional performance tools stop at response times. We measure what matters—customer journeys, drop-offs, and business impact in real-time.',
        component: 'One',
      },
      {
        title: 'Built for Cloud, Serverless & Kubernetes',
        info: 'Kubernetes-aware, serverless-ready performance monitoring. Optimize auto-scaling, latency, and resilience with end-to-end tracing.',
        component: 'Two',
      },
      {
        title: ' AI-Driven & Self-Healing Performance Monitoring',
        info: 'Eliminate manual test creation with AI-powered anomaly detection, dynamic synthetic monitoring, and production traffic mirroring.',
        component: 'Three',
      },
      {
        title: ' Seamless DevOps & AIOps Integration',
        info: 'Embed performance testing into CI/CD pipelines with continuous benchmarking, real-time alerts, and automated issue resolution.',
        component: 'Four',
      },
      {
        title: ' Shift-Right Testing – Align Performance with Business KPIs',
        info: 'Connect performance to revenue. Every millisecond saved means higher conversions, lower churn, and a superior digital experience.',
        component: 'Five',
      },
    ],
    ctaText: 'Book a Demo',
    ctaLink: 'https://calendly.com/vikas-mittal',
  },
  comparisonTable: {
    title: 'GAINSS vs. Traditional Performance Tools',
    image: require('../components/performance-engg/perftools.png'),
    ctaDescription: 'Ready to experience the GAINSS advantage?',
    ctaText: 'Switch to GAINSS Today',
    ctaLink: 'https://calendly.com/vikas-mittal',
  },
  useCases: [
    {
      id: 1,
      title: 'E-commerce & Retail – Prevent Slow Checkout & Cart Abandonment',
      challenge:
        'A global e-commerce brand experiences high cart abandonment rates during peak sales due to slow checkout pages and API failures.',
      solution: [
        'AI-powered performance monitoring identifies slow third-party APIs (payment gateways, fraud checks, etc.).',
        'Smart root cause analysis pinpoints render-blocking scripts and high-latency database calls.',
        'Auto-remediation suggestions recommend async API calls and caching strategies to improve checkout speeds.',
      ],
      outcome:
        'Faster transactions, reduced drop-offs, and increased revenue during high-traffic events.',
    },
    {
      id: 2,
      title:
        'Banking & Financial Services – Ensure Seamless Digital Transactions',
      challenge:
        'A banking app experiences intermittent failures in fund transfers and slow response times in loan approval workflows.',
      solution: [
        'Kubernetes-native performance testing simulates millions of concurrent transactions across auto-scaling cloud infrastructure.',
        'Shift-right testing with production traffic mirroring ensures real-user scenarios are validated in staging.',
        'AI-driven anomaly detection identifies bottlenecks in backend services and fraud detection algorithms.',
      ],
      outcome:
        'Higher transaction success rates, improved customer trust, and faster loan processing.',
    },
    {
      id: 3,
      title: 'SaaS & Cloud Platforms – Improve Multi-Tenant Performance',
      challenge:
        ' A SaaS platform hosting thousands of customers sees performance degradation when multiple tenants onboard new users at once.',
      solution: [
        'AI-driven workload simulation tests multi-tenant concurrency scenarios with real-world traffic patterns.',
        'Dynamic test data parameterization ensures unique datasets are used per tenant, preventing false positives.',
        'Smart test execution auto-scales infrastructure based on load predictions.',
      ],
      outcome:
        'Seamless scaling, optimized resource allocation, and no performance bottlenecks during high-load periods.',
    },
    {
      id: 4,
      title:
        'Media & Streaming – Eliminate Buffering & Optimize Video Playback',
      challenge:
        'A video streaming platform suffers from buffering issues, and users complain about poor video quality during peak hours.',
      solution: [
        'Integrated browser network protocol analysis detects slow CDN performance and latency issues.',
        'AI-based traffic distribution modeling identifies peak usage patterns and recommends adaptive bitrate streaming (ABR) strategies.',
        'Real User Monitoring (RUM) correlates performance data with user location, device type, and internet speed.',
      ],
      outcome:
        'Smoother streaming, reduced buffering, and improved engagement rates.',
    },
    {
      id: 5,
      title: 'Travel & Hospitality – Reduce Booking System Failures',
      challenge:
        ' A travel portal experiences session timeouts and high error rates during holiday season sales.',
      solution: [
        ' AI-powered session replay analyzes failed bookings and checkout errors.',
        'Auto-correlation of API requests prevents session expiration issues due to token mismanagement.',
        'Google Lighthouse & Core Web Vitals identify UI performance issues causing delays.',
      ],
      outcome:
        'Higher successful bookings, fewer support tickets, and better user experience.',
    },
    {
      id: 6,
      title:
        ' Healthcare & Telemedicine – Ensure 100% Uptime for Critical Services',
      challenge:
        ' A telemedicine platform suffers from latency spikes during peak consultation hours, affecting doctor-patient video calls.',
      solution: [
        'Predictive performance modeling forecasts system loads based on appointment scheduling patterns.',
        'AI-driven auto-scaling recommendations ensure backend services scale dynamically.',
        'Accessibility & compliance checks guarantee that the platform meets regulatory performance SLAs.',
      ],
      outcome:
        'Reliable virtual consultations, improved doctor availability, and regulatory compliance.',
    },
  ],
  challengesHeading: {
    main: 'Future-Proof',
    sub: 'Your Performance Strategy with GAINSS',
    description:
      'GAINSS isn’t just another performance tool—it’s an AI-native performance intelligence platform. It helps engineering teams eliminate inefficiencies, predict failures, and enhance digital experiences like never before.',
  },
  challenges: [
    {
      title: 'AI-Native Performance Engineering – Built for the Future',
      body: 'What makes GAINSS AI-Native? Unlike legacy tools that use static scripting and manual configurations, GAINSS is designed to self-learn, self-optimize, and self-heal performance tests using advanced AI & ML models.',
      points: [
        '<strong>AI-Driven Test Optimization</strong> – Continuously refines test scenarios based on real-world production data.',
        '<strong>Predictive Performance Modeling</strong> – Uses historical data to forecast performance issues before they occur.',
        '<strong>Self-Healing Test Scripts</strong> – Automatically updates tests when APIs, page structures, or network conditions change.',
      ],
    },
    {
      title:
        'Autonomous Performance Testing – Shift from Reactive to Proactive',
      body: 'Traditional load testing is reactive. GAINSS is proactive.',
      points: [
        '<strong>Anomaly Detection in Real-Time</strong> – AI scans test results and alerts engineers to potential failures before they escalate.',
        '<strong>Smart Root Cause Analysis</strong> – AI correlates performance degradation with code changes, infrastructure updates, and third-party dependencies.',
        '<strong>Performance Benchmarking with Industry Standards</strong> – Compares your app’s speed, stability, and scalability against top-performing applications.',
      ],
    },
    {
      title:
        'Digital Experience Intelligence – Measure What Users Actually Feel',
      body: 'Beyond Load Testing – See How Real Users Experience Your Application.',
      points: [
        '<strong>AI-Powered UX Insights</strong> – Detect slow-loading elements, render-blocking scripts, and layout shifts impacting Core Web Vitals (LCP, FID, CLS).',
        '<strong>Session Replay & Heatmaps</strong> – AI identifies areas where real users struggle, abandon, or experience delays in workflows.',
        '<strong>SEO & Accessibility Optimization</strong> – Get automated recommendations for search performance and compliance with WCAG standards.',
      ],
    },
    {
      title: 'Role-Based Dashboards – The Right Insights for Every Stakeholder',
      body: 'GAINSS provides personalized dashboards tailored to specific roles',
      points: [
        '<strong>Engineers & Testers</strong> – Real-time logs, API response times, error tracking.',
        '<strong>Performance Leads</strong> – Deep dive into resource utilization, scalability, and failure analysis.',
        '<strong>Head of Engineering & CTOs</strong> – Executive summary with AI-generated action plans, cost impact, and business KPIs.',
      ],
    },
    {
      title: 'AI-Driven Auto-Remediation & Smart Recommendations',
      body: 'GAINSS doesn’t just detect issues—it fixes them.',
      points: [
        '<strong>AI-Powered Fix Suggestions</strong> – Automatically suggests code optimizations, caching strategies, and infrastructure scaling tips.',
        '<strong>Automated Ticketing & Alerting</strong> – Pushes performance issues into JIRA, ServiceNow, or Slack with AI-generated remediation steps.',
        '<strong>Load Test to Production Monitoring Link</strong> – AI compares test results with real production behavior, refining test coverage dynamically.',
      ],
    },
  ],
  sectionButtonLink: 'https://calendly.com/vikas-mittal',
  sectionContent: [
    {
      image: section1,
      position: 'left',
      heading: 'Beyond Load Testing – Measure Real User Experience',
      info: 'Traditional performance tools stop at response times. We measure what matters—customer journeys, drop-offs, and business impact in real-time.',
      lists: [],
    },
    {
      image: section2,
      position: 'right',
      heading: 'Shift-Right Testing – Align Performance with Business KPIs',
      info: 'Connect performance to revenue. Every millisecond saved means higher conversions, lower churn, and a superior digital experience.',
      lists: [],
    },
    {
      image: section3,
      position: 'left',
      heading: 'AI-Powered Automation: No More Tedious Scripting',
      info: 'Reduce scripting efforts by 60% with intelligentAI Features',
      lists: [
        {
          heading: 'Auto-Correlation',
          info: 'Automatically handles dynamic parameters, session tokens, and CSRF tokens, eliminating manual correlation effort.',
        },
        {
          heading: 'Elimination of Redundant HTTP Calls',
          info: 'AI identifies unnecessary API and HTTP calls, streamlining test execution and reducing infrastructure costs.',
        },
        {
          heading: 'Elimination un-wanted traffic',
          info: 'AI identifies third party calls and removes them.',
        },
        {
          heading: 'Automatic transaction and request naming',
          info: 'AI identifies automatically define the name for transaction and requests to have better reporting and understanding of flow.',
        },
        {
          heading: 'Smart Data Parameterization',
          info: 'GAINSS auto-detects input fields and suggests parameterization based on historical execution trends.',
        },
      ],
    },
    {
      image: section4,
      position: 'right',
      heading:
        'GAINSS ensures that every role in your organization gets the insights they need.',
      info: "Whether you're an engineering leader seeking better ROI or a test manager striving for operational excellence, GAINSS has you covered.",
      lists: [
        {
          heading: 'Test Engineers & Test Managers',
          info: 'See real-time KPIs like API response times, error rates, and test execution progress.',
        },
        {
          heading: 'Head of Testing & Performance Leads',
          info: ' Gain deep insights into bottlenecks, user behavior, and workload impact with AI-driven recommendations.',
        },
        {
          heading: 'Head of Engineering & CTOs',
          info: 'Get an action-oriented summary dashboard that connects performance insights to business impact, cost optimization, and user retention.',
        },
      ],
    },
    {
      image: section5,
      position: 'left',
      heading: 'AI Powered Smart performance reporting',
      info: 'Discover the key features that make GAINSS the ultimate test management tool.',
      lists: [
        {
          heading: '',
          info: 'Core performance Metrics in correlation with each other which include responsiveness, throughput, concurrency, error rate etc.',
        },
        {
          heading: '',
          info: 'Real user performance insights from different geographies and different channels on Load.',
        },
        {
          heading: '',
          info: 'Predictive Performance Analysis with performance workload forecasting, failure probability analysis and optimization recommendations',
        },
        {
          heading: '',
          info: 'Customizable dashboards and automated alerts with AI Driven alerting system, custom KPI dashboards, Auto generated Executive reports',
        },
      ],
    },
  ],
  getStartedSection: {
    title: 'Why GAINSS is the AI-Native Solution Your Team Needs',
    subtitle: '',
    description:
      'GAINSS is the only performance engineering solution that integrates synthetic load testing, real user monitoring (RUM), distributed tracing, and AI-driven observability into one powerful platform.',
    points: [
      'Test like a real user – Simulate real-world conditions, not just server load.',
      'Uncover revenue-impacting bottlenecks – Correlate performance with conversions & customer behavior.',
      'Cloud-native & Kubernetes-ready – Designed for microservices, serverless, and auto-scaling architectures.',
      'AI-powered anomaly detection – Predict and prevent failures before they impact users.',
      'Seamless DevOps & CI/CD integration – Shift-left & shift-right testing with actionable insights.',
    ],
    showButton: false,
  },
  formData: {
    reasonQuestion: 'What is your primary reason you are here?',
    reasonOptions: [
      'Need to improve customer experience',
      "Identify what's the optimal cloud capacity we need",
      'Looking for an alternative to Jmeter or similar tools',
      'Want to get a benchmark analysis done',
    ],

    applicationQuestion: 'Which application are you looking to test?',
    applicationOptions: [
      'Mobile first',
      'Web only',
      'Web & Mobile',
      'Backend API or Microservices',
      'Data & AI',
      'Cloud Infrastructure',
      'SaaS Software',
    ],
  },
};

export default performanceEnggContent;
